import { useCopilotAction } from '@copilotkit/react-core'

import { F0QuestionCardMultiStep, useAiChat } from '@factorialco/f0-react'
import type { F0QuestionCardOption, F0QuestionCardStep } from '@factorialco/f0-react'

interface QuestionCardWithSendProps {
  steps: F0QuestionCardStep[]
  skipLabel?: string
  sendAsMessage?: boolean
}

function QuestionCardWithSend({ steps, skipLabel, sendAsMessage }: QuestionCardWithSendProps) {
  const { sendMessage } = useAiChat()
  const showSkip = skipLabel != null && skipLabel !== ''
  const send = sendAsMessage === true

  return (
    <F0QuestionCardMultiStep
      steps={steps}
      sendAsMessage={send}
      onSendMessage={send ? (msg) => sendMessage(msg) : undefined}
      onSkip={showSkip ? () => {} : undefined}
    />
  )
}

const mapOptions = (opts: { id: string; label: string }[]): F0QuestionCardOption[] =>
  opts?.map((o) => ({ id: o.id, label: o.label })) ?? []

/**
 * Renders a question card with multiple-choice checkboxes, pagination, Skip
 * and Next. With `sendAsMessage`, Next sends the selected labels as a new
 * user message to trigger the next workflow turn.
 */
export const useQuestionCardAction = () => {
  useCopilotAction({
    name: 'AiWidgets.UpsellKit.F0QuestionCard',
    description:
      'Display a question card that asks the user a question with multiple checkbox options, pagination (e.g. 1/n), Skip and Next buttons. Use before answering to gather user input. When sendAsMessage is true, Next sends the selected label(s) as a user message to trigger the next workflow turn.',
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: "Optional card title (e.g. 'AI Card Title')",
        required: false,
      },
      {
        name: 'question',
        type: 'string',
        description: 'Question text shown above the options (used when steps is not provided)',
        required: false,
      },
      {
        name: 'options',
        type: 'object[]',
        description:
          'Checkbox options. Each object: { id: string, label: string }. Required when steps is not used.',
        required: false,
        attributes: [
          {
            name: 'id',
            type: 'string',
            description: 'Unique id for the option',
            required: true,
          },
          {
            name: 'label',
            type: 'string',
            description: 'Label text next to the checkbox',
            required: true,
          },
        ],
      },
      {
        name: 'steps',
        type: 'object[]',
        description:
          'Multi-step: array of { question: string, options: { id, label }[] }. When provided, pagination works and Prev/Next switch between steps.',
        required: false,
        attributes: [
          {
            name: 'question',
            type: 'string',
            description: 'Question text for this step',
            required: true,
          },
          {
            name: 'options',
            type: 'object[]',
            description: 'Checkbox options for this step: { id: string, label: string }[]',
            required: true,
            attributes: [
              { name: 'id', type: 'string', required: true },
              { name: 'label', type: 'string', required: true },
            ],
          },
        ],
      },
      {
        name: 'currentStep',
        type: 'number',
        description: "Current step (1-based) for pagination display (e.g. 1 for '1/n')",
        required: false,
      },
      {
        name: 'totalSteps',
        type: 'number',
        description: "Total number of steps for pagination (e.g. 3 for '1/3')",
        required: false,
      },
      {
        name: 'nextLabel',
        type: 'string',
        description: "Label for the Next button (default 'Next')",
        required: false,
      },
      {
        name: 'skipLabel',
        type: 'string',
        description: "Label for the Skip button (default 'Skip').",
        required: false,
      },
      {
        name: 'sendAsMessage',
        type: 'boolean',
        description:
          'When true, clicking Next sends the selected option label(s) as a new user chat message.',
        required: false,
      },
    ],
    available: 'frontend',
    followUp: false,
    render: ({ args: { question, options, steps, skipLabel, sendAsMessage } }) => {
      const resolvedSteps: F0QuestionCardStep[] =
        steps && steps.length > 0
          ? steps.map((step) => ({
              question: step.question,
              options: mapOptions(step.options ?? []),
            }))
          : question != null && options != null
            ? [
                {
                  question,
                  options: mapOptions(options),
                },
              ]
            : []
      return (
        <QuestionCardWithSend
          steps={resolvedSteps}
          skipLabel={skipLabel}
          sendAsMessage={sendAsMessage}
        />
      )
    },
  })
}
